import { db } from '../firebase';
import { collection, query, where, getDocs, updateDoc, doc, addDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { QuerySnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { Dispute, getImpactedTransactions } from './disputeService';
import { MasterTransaction } from '../types';
import { calculateAllocations, saveAllocationsToDb, UnifiedTip } from './allocationService';
import { getSettings } from './settingsService';
import { getLocalDate } from '../lib/utils';
import { handleFirestoreError, OperationType } from '../lib/firestoreError';
import { sendEmail } from '../lib/emailService';

export const getPendingDisputes = async (tenantId: string): Promise<(Dispute & { id: string })[]> => {
  const path = `tenants/${tenantId}/disputes`;
  try {
    const disputesRef = collection(db, 'tenants', tenantId, 'disputes');
    const q = query(disputesRef, where('status', '==', 'Pending'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Dispute & { id: string }));
  } catch (error) {
    handleFirestoreError(error, OperationType.LIST, path);
  }
};

export const approveDisputes = async (
  tenantId: string,
  disputes: (Dispute & { id: string })[],
  managerEmail: string,
  managerComment?: string
) => {
  const path = `tenants/${tenantId}/disputes`;
  try {
    const batch = writeBatch(db);
    const settings = await getSettings(tenantId);
    const today = getLocalDate(settings?.timezone || 'UTC');
    const adjustmentsToAllocate: UnifiedTip[] = [];

    for (const dispute of disputes) {
      const disputeRef = doc(db, 'tenants', tenantId, 'disputes', dispute.id);
      const updateData: any = {
        status: 'Resolved',
        resolvedDate: today,
        approvedBy: managerEmail.split('@')[0],
        managerEmail: managerEmail,
        managerComment: managerComment || dispute.managerNotes || '',
        managerNotes: managerComment || dispute.managerNotes || '',
        resolvedAt: serverTimestamp()
      };

      if (dispute.type === 'Allocation-Correction') {
        updateData.correctionStatus = 'Adjusted';
      }

      batch.set(disputeRef, updateData, { merge: true });

      // If it's a submission correction for a pending tip, we should update the tip directly
      if (dispute.type === 'Submission-Correction' && dispute.originalTipId && dispute.originalTipType) {
        const type = (dispute.originalTipType || '').toLowerCase();
        if (!type) continue;

        const tipCollection = (type === 'cash' || type === 'cash tip') ? 'cash_tips' : 
                            (type === 'jar' || type === 'jar tip') ? 'jar_tips' : null;
        
        if (tipCollection) {
          const tipRef = doc(db, 'tenants', tenantId, tipCollection, dispute.originalTipId);
          console.log(`ALLOCATION_DEBUG: Updating tip in collection: ${tipCollection}, ID: ${dispute.originalTipId}`);
          
          const amountField = (type === 'jar' || type === 'jar tip') ? 'jarAmount' : 'amount';
          batch.set(tipRef, {
            [amountField]: dispute.correctedAmount,
            status: 'Approved',
            notes: (dispute.reason ? `[Disputed: ${dispute.reason}] ` : '') + (dispute.explanation || '')
          }, { merge: true });
        }
      }

      const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
      const adjustment = Math.round((dispute.correctedAmount - dispute.originalAmount) * 100) / 100;
      
      const newTxRef = doc(masterRef);
      
      batch.set(newTxRef, {
        tenantId,
        timestamp: serverTimestamp(),
        originalTipId: dispute.id, 
        parentTxId: dispute.masterTxId || null,
        staffName: dispute.staffName,
        email: dispute.email,
        tipDate: dispute.tipDate,
        shift: dispute.shift || '',
        amount: adjustment,
        type: 'Adjustment',
        source: 'Disputes',
        reportingStatus: 'dispute_submitted',
        notes: `Dispute approved by ${managerEmail} - Type: ${dispute.type}`
      });

      adjustmentsToAllocate.push({
        id: newTxRef.id,
        amount: adjustment,
        tipDate: dispute.tipDate,
        staffName: dispute.staffName, 
        email: dispute.email,
        shift: dispute.shift || '',
        source: 'Calculated', 
        masterTxId: newTxRef.id,
        type: 'Submitted'
      });
    }

    await batch.commit();

    // Send approval resolution notifications
    for (const dispute of disputes) {
      try {
        const subject = `[Dispute Approved] Tip Correction Approved for ${dispute.tipDate}`;
        const textMsg = `Hi ${dispute.staffName},\n\nYour tip dispute for ${dispute.tipDate} (${dispute.shift}) has been APPROVED by ${managerEmail}.\n\nOriginal Amount: $${dispute.originalAmount.toFixed(2)}\nApproved Amount: $${dispute.correctedAmount.toFixed(2)}\nManager Comment: ${managerComment || 'Approved'}\n\nThank you,\nTipValue AI Management`;
        const htmlMsg = `
          <div style="font-family: sans-serif; padding: 20px; color: #1f2937;">
            <h2 style="color: #059669; margin-top: 0;">✅ Tip Dispute Approved</h2>
            <p>Hi <strong>${dispute.staffName}</strong>,</p>
            <p>Your dispute regarding tip allocation on <strong>${dispute.tipDate}</strong> (${dispute.shift}) has been approved.</p>
            <div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <p style="margin: 0 0 5px 0;"><strong>Original Amount:</strong> $${dispute.originalAmount.toFixed(2)}</p>
              <p style="margin: 0 0 5px 0; color: #15803d; font-size: 1.1em;"><strong>New Approved Amount:</strong> $${dispute.correctedAmount.toFixed(2)}</p>
              <p style="margin: 0;"><strong>Manager Comment:</strong> ${managerComment || 'Approved as requested.'}</p>
            </div>
            <p>The adjustment has been added to the master ledger and will be included in your upcoming payroll period.</p>
          </div>
        `;

        if (dispute.email) {
          await sendEmail(dispute.email, subject, textMsg, htmlMsg, dispute.id, tenantId);
        }

        // Notify other impacted staff
        if (dispute.masterTxId) {
          const impacted = await getImpactedTransactions(tenantId, dispute.masterTxId);
          for (const rawTx of impacted) {
            const tx = rawTx as MasterTransaction;
            if (tx.email && tx.email !== dispute.email) {
              const impSubject = `[TipValue AI] Tip Pool Adjustment - ${dispute.tipDate}`;
              const impHtml = `
                <div style="font-family: sans-serif; padding: 20px; color: #1f2937;">
                  <h2 style="color: #6366f1; margin-top: 0;">Tip Pool Adjustment</h2>
                  <p>Hi <strong>${tx.staffName}</strong>,</p>
                  <p>A correction was made to a tip pool you participated in for <strong>${dispute.tipDate}</strong> (${dispute.shift}).</p>
                  <p>This adjustment may have impacted your final allocation for that shift.</p>
                  <p>You can view your updated shift details in your Tip History dashboard.</p>
                </div>
              `;
              await sendEmail(tx.email, impSubject, 'Tip Pool Adjustment', impHtml, dispute.id, tenantId);
            }
          }
        }

        await addDoc(collection(db, 'tenants', tenantId, 'messages'), {
          tenantId,
          senderUid: 'system',
          senderName: 'Management',
          senderRole: 'Manager',
          subject: `Dispute Approved - ${dispute.tipDate}`,
          body: textMsg,
          type: 'DisputeApproved',
          status: 'Unread',
          disputeId: dispute.id,
          recipientEmail: dispute.email,
          timestamp: serverTimestamp()
        });
      } catch (notifyErr) {
        console.warn(`Failed to send dispute approval notification for ${dispute.id}:`, notifyErr);
      }
    }

    // Now trigger allocation
    if (adjustmentsToAllocate.length > 0) {
      const allocations = await calculateAllocations(tenantId, adjustmentsToAllocate);
      await saveAllocationsToDb(tenantId, allocations, adjustmentsToAllocate);
    }
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, `tenants/${tenantId}/disputes`);
  }
};

export const rejectDisputes = async (
  tenantId: string,
  disputes: (Dispute & { id: string })[],
  managerEmail: string,
  managerComment?: string
) => {
  const path = `tenants/${tenantId}/disputes`;
  try {
    const batch = writeBatch(db);
    const settings = await getSettings(tenantId);
    const today = getLocalDate(settings?.timezone || 'UTC');

    for (const dispute of disputes) {
      const disputeRef = doc(db, 'tenants', tenantId, 'disputes', dispute.id);
      const updateData: any = {
        status: 'Rejected',
        resolvedDate: today,
        resolvedBy: managerEmail.split('@')[0], 
        managerEmail: managerEmail,
        managerComment: managerComment || dispute.managerNotes || '',
        managerNotes: managerComment || dispute.managerNotes || '',
        resolvedAt: serverTimestamp()
      };

      batch.set(disputeRef, updateData, { merge: true });
    }

    await batch.commit();

    // Send rejection resolution notifications
    for (const dispute of disputes) {
      try {
        const subject = `[Dispute Update] Decision regarding tip dispute for ${dispute.tipDate}`;
        const textMsg = `Hi ${dispute.staffName},\n\nYour tip dispute for ${dispute.tipDate} (${dispute.shift}) was reviewed by ${managerEmail} and was not approved.\n\nManager Comment: ${managerComment || 'No comment provided.'}\n\nPlease reach out to management if you have any questions.`;
        const htmlMsg = `
          <div style="font-family: sans-serif; padding: 20px; color: #1f2937;">
            <h2 style="color: #dc2626; margin-top: 0;">Notice Regarding Tip Dispute</h2>
            <p>Hi <strong>${dispute.staffName}</strong>,</p>
            <p>Your dispute regarding the tip allocation on <strong>${dispute.tipDate}</strong> (${dispute.shift}) was reviewed by management and has been marked as <strong>Rejected</strong>.</p>
            <div style="background-color: #fef2f2; border: 1px solid #fecaca; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <p style="margin: 0 0 5px 0;"><strong>Original Amount:</strong> $${dispute.originalAmount.toFixed(2)}</p>
              <p style="margin: 0 0 5px 0;"><strong>Requested Amount:</strong> $${dispute.correctedAmount.toFixed(2)}</p>
              <p style="margin: 0; color: #991b1b;"><strong>Manager Notes:</strong> ${managerComment || 'Reviewed and rejected by manager.'}</p>
            </div>
            <p>If you believe there is additional context, please contact your store manager.</p>
          </div>
        `;

        if (dispute.email) {
          await sendEmail(dispute.email, subject, textMsg, htmlMsg, dispute.id, tenantId);
        }

        await addDoc(collection(db, 'tenants', tenantId, 'messages'), {
          tenantId,
          senderUid: 'system',
          senderName: 'Management',
          senderRole: 'Manager',
          subject: `Dispute Rejected - ${dispute.tipDate}`,
          body: textMsg,
          type: 'DisputeRejected',
          status: 'Unread',
          disputeId: dispute.id,
          recipientEmail: dispute.email,
          timestamp: serverTimestamp()
        });
      } catch (notifyErr) {
        console.warn(`Failed to send dispute rejection notification for ${dispute.id}:`, notifyErr);
      }
    }
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, `tenants/${tenantId}/disputes`);
  }
};
